import { IDropdownOption, MessageBarType } from '@fluentui/react';
import { Button, buttonStylesPrimary, Dropdown, MessageBar, useTheme } from '@h2oai/ui-kit';
import { useEffect, useRef, useState } from 'react';

import { AiUnitMaxStatsRequest, AiUnitMaxStatsResponse } from '../../admin-center/gen/aiUnitMax/aiUnitMax_pb';
import { EnvIdRequest } from '../../admin-center/gen/environment_info/natgw_pb';
import { useAdminCenterService } from '../../admin-center/hooks';
import { messageBarContainerStyles } from './common/common.styles';
import { useTimeFilters } from './common/hooks';
import { formatDateLocal } from './common/utils';
import {
  AiUnitConsumptionTopStyles,
  AiUnitMaxStyles,
  InfoLabel,
  InfoValue,
} from './components/AIUnitConsumption/AIUnitConsumption.styles';
import AlertSidePanel from './components/AIUnitConsumption/AlertSidePanel';
import ConsumptionPlot from './components/AIUnitConsumption/ConsumptionPlot';
import TimeFilters from './components/TimeFilters/TimeFilters';
import { AiUnitMaxTimeFrame, AiUnitMaxTimeFrameKey } from './constants';
import PageWrapper from './PageWrapper';

const timeFrames = [
  {
    key: AiUnitMaxTimeFrameKey.daily,
    text: AiUnitMaxTimeFrame.daily,
  },
  {
    key: AiUnitMaxTimeFrameKey.weekly,
    text: AiUnitMaxTimeFrame.weekly,
  },
  {
    key: AiUnitMaxTimeFrameKey.monthly,
    text: AiUnitMaxTimeFrame.monthly,
  },
];

export default function AIUnitConsumption() {
  const service = useAdminCenterService();
  const theme = useTheme();
  const [isSidePanelOpen, setSidePanelOpen] = useState(false);

  const cachedEnvId = useRef<string | null>(null);
  const envIdPromise = useRef<Promise<string> | null>(null);
  const [envId, setEnvId] = useState(localStorage.getItem('envId') || null);

  const fetchEnvId = async () => {
    const cachedValue = localStorage.getItem('envId');
    if (cachedValue) {
      setEnvId(cachedValue);
      cachedEnvId.current = cachedValue;
      return;
    }

    if (!envIdPromise.current) {
      envIdPromise.current = (async () => {
        try {
          const envIdRequest: EnvIdRequest = {};
          const envIdResponse = await service.getEnvId(envIdRequest);
          const id = envIdResponse.envId || '';
          cachedEnvId.current = id;
          return id;
        } catch (error) {
          envIdPromise.current = null;
          throw error;
        }
      })();
    }

    try {
      const id = await envIdPromise.current;
      setEnvId(id);
      localStorage.setItem('envId', id);
    } catch (error) {
      console.error('Error fetching envId:', error);
    }
  };

  useEffect(() => {
    fetchEnvId();
  }, []);

  const handleAddButtonClick = () => {
    setSidePanelOpen(true);
  };

  const [selectedTimeFrameKey, setSelectedTimeFrameKey] = useState<string>(AiUnitMaxTimeFrameKey.daily);
  const onTimeFrameChange = (_event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
    if (item) {
      setSelectedTimeFrameKey(item.key as string);
    }
  };

  const [stats, setStats] = useState<AiUnitMaxStatsResponse | null>(null);
  const [isPercentageHigh, setIsPercentageHigh] = useState<boolean>(true);
  const [alertResponseMessage, setAlertResponseMessage] = useState<string | null>(null);
  const [showMessageBar, setShowMessagebar] = useState<boolean>(false);
  const [isAlertRequestSuccess, setIsAlertRequestSuccess] = useState<boolean>(false);
  const { startDate, endDate, selectedTimeFrame, handleTimeFilterChange } = useTimeFilters();

  const fetchAiUnitMaxStats = async () => {
    try {
      const request: AiUnitMaxStatsRequest = {};
      const response = await service.getAiUnitMaxStats(request);
      setStats(response);

      const maxAiUnitsConsumed = response?.totalMaxAiUnits;
      if (maxAiUnitsConsumed == null || isNaN(Number(maxAiUnitsConsumed))) {
        throw new Error('Invalid response: totalMaxAiUnits is missing or not a valid number.');
      }

      if (response?.purchasedAiUnits && response.purchasedAiUnits !== 0) {
        const percentageUsed = (maxAiUnitsConsumed / response?.purchasedAiUnits) * 100;
        setIsPercentageHigh(percentageUsed > 90);
      } else {
        setIsPercentageHigh(false);
      }
    } catch (error) {
      console.error('Error fetching login statistics:', error);
    }
  };

  useEffect(() => {
    fetchAiUnitMaxStats();
  }, []);

  function setAlertMessageBar(isSuccess: boolean) {
    if (isSuccess) {
      setIsAlertRequestSuccess(true);
      setAlertResponseMessage('AI unit usage alert data updated successfully');
    } else {
      setIsAlertRequestSuccess(false);
      setAlertResponseMessage('An error occured while updating AI unit usage alert data');
    }
    setShowMessagebar(true);
  }

  return (
    <PageWrapper>
      {showMessageBar && (
        <div style={messageBarContainerStyles}>
          <MessageBar
            timeout={4000}
            onDismiss={() => setShowMessagebar(false)}
            messageBarType={isAlertRequestSuccess ? MessageBarType.success : MessageBarType.error}
          >
            {alertResponseMessage}
          </MessageBar>
        </div>
      )}
      <div style={AiUnitConsumptionTopStyles}>
        <div style={{ position: 'relative', marginBottom: '1rem' }}>
          <Button styles={buttonStylesPrimary} text="Consumption Alerts" onClick={handleAddButtonClick} />
          {isSidePanelOpen && (
            <div style={{ position: 'absolute', top: 0, right: 0 }}>
              <AlertSidePanel
                isOpen={isSidePanelOpen}
                setOpen={setSidePanelOpen}
                setAlertMessageBar={setAlertMessageBar}
                envId={envId}
              />
            </div>
          )}
        </div>
      </div>
      <div style={AiUnitMaxStyles(theme)}>
        <div>
          <p style={InfoLabel(theme)}>Purchased AI Unit:</p>
          <p style={InfoValue(theme)}>{stats?.purchasedAiUnits ?? 0}</p>
        </div>
        <div>
          <p style={InfoLabel(theme)}>Maximum Capacity Used:</p>
          <p style={InfoValue(theme)}>{stats?.totalMaxAiUnits ?? '-'}</p>
        </div>
        <div>
          <p style={InfoLabel(theme)}>Overall Maximum Consumption Percentage:</p>
          <p style={InfoValue(theme)}>
            <span style={{ color: isPercentageHigh ? 'red' : 'green' }}>
              {isPercentageHigh ? 'More than 90%' : 'Less than 90%'}
            </span>{' '}
            of the purchased units
          </p>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          marginBottom: '1rem',
        }}
      >
        <Dropdown
          placeholder="Select granularity"
          options={timeFrames}
          selectedKey={selectedTimeFrameKey}
          onChange={onTimeFrameChange}
          styles={{ root: { width: 140 } }}
        />
        <TimeFilters
          startDate={startDate}
          endDate={endDate}
          selectedTimeFrame={selectedTimeFrame}
          onChange={handleTimeFilterChange}
          styles={{
            filterContainer: {
              marginLeft: 'auto',
              width: 'fit-content',
            },
          }}
        />
      </div>

      <div>
        <ConsumptionPlot
          startDate={formatDateLocal(startDate)}
          endDate={formatDateLocal(endDate)}
          timeFrameKey={selectedTimeFrameKey}
        />
      </div>
    </PageWrapper>
  );
}
